import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  ViewChildren,
  QueryList,
  ElementRef,
  signal,
  effect,
  OnDestroy,
} from '@angular/core';
import { HorizontalTreeCardComponent } from './horizontal-tree-card/horizontal-tree-card.component';
import { TreeNode } from './horizontal-tree.model';
import { NgTemplateOutlet } from '@angular/common';
import { Subject, takeUntil, throttleTime, distinctUntilChanged } from 'rxjs';
import { LoaderComponent } from '../../../../ui-components/lib/loader/loader.component';

@Component({
  selector: 'lib-horizontal-tree',
  imports: [HorizontalTreeCardComponent, LoaderComponent, NgTemplateOutlet],
  templateUrl: './horizontal-tree.component.html',
  styleUrl: './horizontal-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class HorizontalTreeComponent implements OnDestroy {
  @ViewChildren('verticalLine') verticalLines!: QueryList<ElementRef>;
  @ViewChildren('treeCard') treeCards!: QueryList<ElementRef>;

  levels = input.required<number[]>();
  nodesPerLevel = input.required<Record<number, TreeNode[]>>();

  isLoadingAtLevel = input<Record<number, boolean>>({ 0: false });
  canLoadMoreAtLevel = input<(level: number) => boolean>();
  isNoneSelectedAtLevel = input<Record<number, boolean>>({ 0: false });
  showNodeTitle = input<boolean>(true);
  translations = input<{
    loadMoreButtonLabel: string;
  }>({
    loadMoreButtonLabel: 'Load More',
  });

  selectedCard = output<TreeNode>();
  loadMoreAtLevel = output<number>();

  nodeTimeout = signal<any>(undefined);

  private scrollSubject = new Subject<Event>();
  private destroy$ = new Subject<void>();

  constructor() {
    effect(() => this.updateVerticalLinesAfterApiResponse());
    this.setupScrollOptimization();
  }

  private setupScrollOptimization(): void {
    // Use throttleTime for smooth scrolling performance
    this.scrollSubject
      .pipe(
        throttleTime(50, undefined, { leading: true, trailing: true }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateVerticalLines();
      });
  }

  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  updateVerticalLinesAfterApiResponse() {
    const isLoading = Object.values(this.isLoadingAtLevel()).some(
      value => value
    );
    if (!isLoading) {
      this.nodeTimeout.set(
        setTimeout(() => {
          requestAnimationFrame(() => this.updateVerticalLines());
        }, 100)
      );
    }
  }

  onCardClick(event: TreeNode) {
    this.selectedCard.emit(event);
  }

  onLoadMore(level: number) {
    this.loadMoreAtLevel.emit(level);
  }

  updateVerticalLines() {
    const lines =
      this.verticalLines?.toArray().map(ref => ref.nativeElement) || [];
    lines.forEach((lineElement, index) => {
      this.processVerticalLine(lineElement, index);
    });
  }

  private processVerticalLine(lineElement: HTMLElement, index: number): void {
    const parentId = lineElement.getAttribute('data-parent-id');
    if (!parentId || index === null || index === undefined) return;

    const parentCardElement = document.querySelector(
      `lib-horizontal-tree-card[data-node-id="${parentId}"]`
    );
    if (!parentCardElement) return;

    // Try to resolve the child card associated with this vertical line
    const childId = lineElement.getAttribute('data-node-id');
    let childCardElement: Element | null =
      lineElement.parentElement?.querySelector('lib-horizontal-tree-card') ||
      null;
    if (!childCardElement && childId) {
      childCardElement = document.querySelector(
        `lib-horizontal-tree-card[data-node-id="${childId}"]`
      );
    }
    if (!childCardElement) return;

    this.applyCroppedHeight(lineElement, parentCardElement, childCardElement);
  }

  private applyCroppedHeight(
    lineElement: HTMLElement,
    parentCardElement: Element,
    childCardElement: Element
  ): void {
    const parentRect = parentCardElement.getBoundingClientRect();
    const childRect = childCardElement.getBoundingClientRect();
    const wrapperEl = lineElement.closest('.tree-level') as HTMLElement;
    if (!wrapperEl) return;

    const wrapperRect = wrapperEl.getBoundingClientRect();

    const parentCenter = parentRect.top + parentRect.height / 2;
    const childCenter = childRect.top + childRect.height / 2;

    const heightPx = Math.abs(parentCenter - childCenter);
    const heightRem = heightPx / 16; // px -> rem

    lineElement.style.height = `${heightRem}rem`;

    const isRtl = this.isRtl(wrapperEl);
    const parentCenterX = parentRect.left + parentRect.width / 2;
    const childCenterX = childRect.left + childRect.width / 2;
    const isRightSide = isRtl
      ? childCenterX <= parentCenterX
      : childCenterX >= parentCenterX;
    if (isRightSide) {
      this.applyRightSideOffsets(
        lineElement,
        parentCenter,
        childCenter,
        wrapperRect
      );
    } else {
      this.applyLeftSideOffsets(
        lineElement,
        parentCenter,
        childCenter,
        wrapperRect
      );
    }
  }

  private isRtl(element: Element): boolean {
    const computedDir = getComputedStyle(element as HTMLElement).direction;
    const documentDir = document.documentElement.dir;
    return computedDir === 'rtl' || documentDir === 'rtl';
  }

  private applyRightSideOffsets(
    lineElement: HTMLElement,
    parentCenter: number,
    childCenter: number,
    wrapperRect: DOMRect
  ): void {
    const bottomOffsetRem = Math.min(
      2,
      (wrapperRect.bottom - parentCenter) / 16
    );
    const topOffsetRem = Math.max(2, (childCenter - wrapperRect.bottom) / 16);
    if (parentCenter < childCenter) {
      lineElement.style.bottom = `${bottomOffsetRem}rem`;
      lineElement.style.top = 'unset';
    } else {
      lineElement.style.top = `${topOffsetRem}rem`;
      lineElement.style.bottom = 'unset';
    }
  }

  private applyLeftSideOffsets(
    lineElement: HTMLElement,
    parentCenter: number,
    childCenter: number,
    wrapperRect: DOMRect
  ): void {
    const topOffsetRem = Math.min(2, (parentCenter - wrapperRect.top) / 16);
    if (parentCenter < childCenter) {
      lineElement.style.top = `${topOffsetRem}rem`;
      lineElement.style.bottom = 'unset';
    } else {
      lineElement.style.bottom = `${topOffsetRem}rem`;
      lineElement.style.top = 'unset';
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.nodeTimeout());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
